<template>
  <div>
    <!-- IMPORTANT, do not remove client-only component -->
    <!-- Removing will cause rehydration server side rendering issues -->
    <!-- Talk to (kevin.lee) for more information -->
    <client-only>
      <SplashHero
        :white-logo="whiteLogo"
        :white-logo-size="whiteLogoSize"
        :company-name="companyName"
        :promotion="currentPromo"
      />
      <SplashSubHero :items="subHeroItems" class="mb-12 sm:mb-14" />
      <SplashVideo />
      <SplashIntermediate class="mb-12" />
      <SplashFeatures />
      <SplashTestimonial class="mb-12" />
      <SplashSubFooter />
      <SplashModals
        :color-logo="colorLogo"
        :color-logo-size="colorLogoSize"
        :company-name="companyName"
        :customer-type="customerType"
        :discount="discount"
        :promotion="currentPromo"
      />
    </client-only>
  </div>
</template>

<script>
import {
  defineComponent,
  onBeforeMount,
  useMeta,
  onMounted,
  computed,
  useRoute,
} from '@nuxtjs/composition-api';
import Cookie from 'js-cookie';
import SplashFeatures from './SplashFeatures';
import SplashHero from './SplashHero';
import SplashIntermediate from './SplashIntermediate';
import SplashModals from './SplashModals';
import SplashSubFooter from './SplashSubFooter';
import SplashSubHero from './SplashSubHero';
import SplashTestimonial from './SplashTestimonial';
import SplashVideo from './SplashVideo';
import Campaign, {
  promotionType,
} from '@/modules/SplashPage/constants/Campaign';
import useGlobalSettings from '@/hooks/useGlobalSettings';
import SubHeroIcons from '@/constants/SubHeroIcons';
import usePartnerPromo from '@/modules/SplashPage/hooks/usePartnerPromo';
import CookieNames from '@/constants/CookieNames';
import Partners from '@/modules/SplashPage/constants/Partners';

const FreeTrialBanner = () => import('./FreeTrial/FreeTrialBanner');
const RadioBanner = () => import('./RadioBanner');
const PartnerBanner = () => import('./Partners/PartnerBanner');

export default defineComponent({
  name: 'SplashPage',
  components: {
    SplashFeatures,
    SplashHero,
    SplashIntermediate,
    SplashModals,
    SplashSubFooter,
    SplashSubHero,
    SplashTestimonial,
    SplashVideo,
  },
  layout: 'HomeSplash',
  props: {
    whiteLogo: {
      type: String,
      default: '',
    },
    whiteLogoSize: {
      type: String,
      default: '',
    },
    colorLogo: {
      type: String,
      default: '',
    },
    colorLogoSize: {
      type: String,
      default: '',
    },
    companyName: {
      type: String,
      default: '',
    },
    customerType: {
      type: String,
      default: 'Customer',
    },
    discount: {
      type: Number,
      default: 50,
    },
    promotion: {
      type: String,
      default: promotionType.SETUP_FEE_DISCOUNT,
    },
  },
  setup(props) {
    const { isAPartnerPath, shouldShow, isJPMorgan } = usePartnerPromo(props);
    const route = useRoute();
    const { setHeader } = useGlobalSettings();

    const currentPromo = computed(() =>
      isJPMorgan.value
        ? promotionType.THREE_MONTH_DOLLAR_TRIAL
        : props.promotion
    );

    onMounted(() => {
      const currentBrand =
        props.companyName?.replaceAll(' ', '-')?.toLowerCase() ||
        route.value.name;
      // This cookie is used later to track discounts.
      Cookie.set(CookieNames.REFERRED_BY_CODE, currentBrand);
      Cookie.set(CookieNames.PARTNER_REFERRAL_CODE, currentBrand);

      if (route.value.name === Partners.ACCOUNTANT) {
        Cookie.set('source', 'Partner');
      }
    });

    onBeforeMount(() => {
      setHeader({
        noHero: true,
      });
    });

    const banner = computed(() => {
      if (route.value.name === Campaign.FREE_TRIAL) {
        return FreeTrialBanner;
      }
      if (
        route.value.query.utm_medium === 'sem-br' ||
        route.value.name === Campaign.RADIO
      ) {
        return RadioBanner;
      }
      if (shouldShow.value || isAPartnerPath.value) {
        return PartnerBanner;
      }

      return null;
    });

    useMeta(() => ({
      title: `Bambee: Get A Dedicated HR Manager For $99/month.`,
    }));

    return {
      banner,
      currentPromo,
    };
  },

  head() {},
  computed: {
    subHeroItems() {
      const items = SubHeroIcons;

      return this.$matchMedia.lg ? items : items.slice(0, -1);
    },
    brand() {
      return this.$route.params.brand || this.$route.name;
    },
  },
});
</script>
